import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  MagazineCreateSchema,
  MagazineQueryAllSchema,
  MagazineQuerySchema,
  MagazineUpdateSchema,
} from './schemas';

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['MAGAZINES'] });
};

export const useGetMagazinesMutation: UseControlledMutation<MagazineQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['MAGAZINES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<MagazineQueryAllSchema>({
            endpoint: '/api/v1/magazines',
            params: { search, per_page },
          });
          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetMagazines(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['MAGAZINES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<MagazineQueryAllSchema>({
        endpoint: '/api/v1/magazines',
        params: `${apiParamStr}&include=clientsCount,icon`,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, MagazineCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/magazines/',
        payload,
      }),
  });

export function useGetMagazine(id: string) {
  return useSuspenseQuery({
    queryKey: ['MAGAZINE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: MagazineQuerySchema }>({
        endpoint: `/api/v1/magazines/${id}`,
        params: { include: 'icon' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: MagazineQuerySchema }, ApiError, Partial<MagazineUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/magazines/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['MAGAZINE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/magazines/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
